/**
 * MHARHER Notification Settings Component
 * Full notification preferences management UI
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { 
  Bell, BellOff, Mail, Smartphone, MessageCircle, Heart, 
  Users, Trophy, Flame, Star, Clock, Moon, Send, Check,
  ChevronRight, AlertCircle, Loader2
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export default function NotificationSettings() {
  const { t } = useTranslation();
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification
  } = usePushNotifications();

  const [localPrefs, setLocalPrefs] = useState({
    push_enabled: true,
    email_enabled: true,
    follows: true,
    likes: true,
    comments: true,
    challenges: true,
    challenge_reminders: true,
    barka_updates: true,
    level_ups: true,
    daily_reminder: false,
    weekly_digest: true,
    quiet_hours_enabled: false,
    quiet_start: "22:00",
    quiet_end: "08:00"
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load preferences
  useEffect(() => {
    if (preferences) {
      setLocalPrefs(prev => ({ ...prev, ...preferences }));
    }
  }, [preferences]);

  // Track changes
  useEffect(() => {
    if (preferences) {
      const changed = JSON.stringify(localPrefs) !== JSON.stringify(preferences);
      setHasChanges(changed);
    }
  }, [localPrefs, preferences]);

  const handleToggle = (key) => {
    setLocalPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectChange = (key, value) => {
    setLocalPrefs(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await updatePreferences(localPrefs);
    setSaving(false);
  };

  const handlePushToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
      setLocalPrefs(prev => ({ ...prev, push_enabled: false }));
    } else {
      const success = await subscribe();
      if (success) {
        setLocalPrefs(prev => ({ ...prev, push_enabled: true }));
      }
    }
  };

  // Notification categories
  const categories = [
    {
      title: t('notifications.social', 'Interactions sociales'),
      icon: Users,
      color: 'text-blue-500',
      items: [
        { key: 'follows', label: t('notifications.newFollower', 'Nouveau abonné'), icon: Users },
        { key: 'likes', label: t('notifications.likes', 'J\'aime sur tes posts'), icon: Heart },
        { key: 'comments', label: t('notifications.comments', 'Commentaires'), icon: MessageCircle }
      ]
    },
    {
      title: t('notifications.challenges', 'Défis'),
      icon: Trophy,
      color: 'text-yellow-500',
      items: [
        { key: 'challenges', label: t('notifications.challengeNews', 'Nouveaux défis'), icon: Trophy },
        { key: 'challenge_reminders', label: t('notifications.challengeReminders', 'Rappels de fin de défi'), icon: Clock }
      ]
    },
    {
      title: t('notifications.gamification', 'Progression'),
      icon: Flame,
      color: 'text-orange-500',
      items: [
        { key: 'barka_updates', label: t('notifications.barkaEarned', 'BARKA gagnés'), icon: Flame },
        { key: 'level_ups', label: t('notifications.levelUp', 'Nouveau niveau'), icon: Star }
      ]
    }
  ];

  return (
    <div className="space-y-6" data-testid="notification-settings">
      {/* Push Notifications Status Card */}
      <Card className={`border-2 ${isSubscribed ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isSubscribed ? (
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <BellOff className="w-6 h-6 text-gray-500" />
                </div>
              )}
              <div>
                <CardTitle className="text-lg">
                  {t('notifications.push', 'Notifications Push')}
                </CardTitle>
                <CardDescription>
                  {isSubscribed 
                    ? t('notifications.pushActive', 'Activées sur cet appareil')
                    : t('notifications.pushInactive', 'Désactivées')
                  }
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={isSubscribed}
              onCheckedChange={handlePushToggle}
              disabled={loading || !isSupported}
              data-testid="push-toggle"
            />
          </div>
        </CardHeader>
        
        {!isSupported && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">
                {t('notifications.notSupported', 'Les notifications push ne sont pas supportées par ce navigateur.')}
              </p>
            </div>
          </CardContent>
        )}
        
        {isSupported && permission === 'denied' && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">
                {t('notifications.permissionDenied', 'Les notifications sont bloquées. Active-les dans les paramètres de ton navigateur.')}
              </p>
            </div>
          </CardContent>
        )}

        {isSubscribed && (
          <CardContent className="pt-0">
            <Button
              variant="outline"
              size="sm"
              onClick={sendTestNotification}
              disabled={loading}
              className="w-full"
              data-testid="test-notification-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {t('notifications.sendTest', 'Envoyer une notification test')}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Email Notifications */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {t('notifications.email', 'Notifications Email')}
                </CardTitle>
                <CardDescription>
                  {t('notifications.emailDesc', 'Reçois les notifications importantes par email')}
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={localPrefs.email_enabled}
              onCheckedChange={() => handleToggle('email_enabled')}
              data-testid="email-toggle"
            />
          </div>
        </CardHeader>
        
        {localPrefs.email_enabled && (
          <CardContent className="pt-0 space-y-4">
            <div className="flex items-center justify-between py-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                {t('notifications.dailyReminder', 'Rappel quotidien')}
              </Label>
              <Switch
                checked={localPrefs.daily_reminder}
                onCheckedChange={() => handleToggle('daily_reminder')}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-500" />
                {t('notifications.weeklyDigest', 'Résumé hebdomadaire')}
              </Label>
              <Switch
                checked={localPrefs.weekly_digest}
                onCheckedChange={() => handleToggle('weekly_digest')}
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Notification Categories */}
      {categories.map((category, idx) => (
        <Card key={idx}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center`}>
                <category.icon className={`w-5 h-5 ${category.color}`} />
              </div>
              <CardTitle className="text-base">{category.title}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1">
              {category.items.map((item, itemIdx) => (
                <div 
                  key={item.key}
                  className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Label className="flex items-center gap-3 cursor-pointer">
                    <item.icon className="w-4 h-4 text-gray-400" />
                    <span>{item.label}</span>
                  </Label>
                  <Switch
                    checked={localPrefs[item.key]}
                    onCheckedChange={() => handleToggle(item.key)}
                    data-testid={`toggle-${item.key}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Quiet Hours */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Moon className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">
                  {t('notifications.quietHours', 'Heures silencieuses')}
                </CardTitle>
                <CardDescription className="text-sm">
                  {t('notifications.quietHoursDesc', 'Pas de notifications push pendant ces heures')}
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={localPrefs.quiet_hours_enabled}
              onCheckedChange={() => handleToggle('quiet_hours_enabled')}
              data-testid="quiet-hours-toggle"
            />
          </div>
        </CardHeader>
        
        {localPrefs.quiet_hours_enabled && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-gray-500">
                  {t('notifications.from', 'De')}
                </Label>
                <Select
                  value={localPrefs.quiet_start}
                  onValueChange={(v) => handleSelectChange('quiet_start', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0') + ':00';
                      return <SelectItem key={hour} value={hour}>{hour}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-gray-500">
                  {t('notifications.to', 'À')}
                </Label>
                <Select
                  value={localPrefs.quiet_end}
                  onValueChange={(v) => handleSelectChange('quiet_end', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0') + ':00';
                      return <SelectItem key={hour} value={hour}>{hour}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky bottom-4 bg-white/80 backdrop-blur-sm p-4 rounded-xl border shadow-lg">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#1E4ED8] hover:bg-[#1842B8]"
            data-testid="save-preferences-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.saving', 'Enregistrement...')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('common.saveChanges', 'Enregistrer les modifications')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
